<template>
  <section class="stock-overview">
    <h2 class="tracking-normal font-semibold text-xl mb-4 lg_text-2xl">Stock Overview</h2>
    <div v-if="company.last_quote" class="stock-info mb-4" data-testid="stock-info">
      <p class="text-lg leading-none mb-0 col-span-full md_col-start-3 md_justify-self-end">
        {{ company.stock_symbol }} • {{ company.name }}
      </p>
      <p class="text-5xl leading-none mb-0 row-span-2 md_row-span-full" data-testid="current-stock-price">
        ${{ formatTradeValue(company.last_quote.last) }}
      </p>
      <CompaniesStockChange class="px-4 py-1" :stock="company.last_quote" />
      <p class="mb-0 text-xs text-gray-600 md_col-start-3 md_justify-self-end">
        {{ formatDateTimeFull(company.last_quote.date) }} · {{ company.last_quote.currency }}
      </p>
    </div>
    <Chart v-if="stocks.length > 0" class="rounded-lg" constructor-type="stockChart" :options="chartOptions as Options" />
  </section>
</template>

<script lang="ts" setup>
import { Chart } from 'highcharts-vue'
import Highcharts from 'highcharts'
import type { Options } from 'highcharts'
import type { Stock } from '@/entities/stock'
import type { Company, IncludeLastQuote } from '@/entities/company'
import { formatTradeValue } from '@/lib/utils'
import { formatDateTimeFull } from '@/lib/datetime-format-utils'
import type { DeepReadonly } from 'vue'

const props = defineProps({
  company: {
    type: Object as () => Company & IncludeLastQuote,
    required: true,
  },
  stocks: {
    type: Array as () => DeepReadonly<Stock[]>,
    default: () => [],
  },
})

const chartOptions = computed(() => ({
  type: 'area',
  credits: { enabled: false },
  navigator: { enabled: false },
  rangeSelector: { enabled: true, inputEnabled: false, selected: 4 },
  scrollbar: { enabled: false },

  plotOptions: { series: { color: '#3F808C' } },
  tooltip: {
    formatter(this: Highcharts.TooltipFormatterContextObject, tooltip: any) {
      if (this.y && this.x && typeof this.x === 'number' && this.points?.length) {
        const point = this.points[0]
        return [
          `<span style="font-size: 10px">${Highcharts.dateFormat('%A, %B %d', this.x)}</span><br/>`,
          `<span style="color:${point.color}">●</span> ${point.series.name}: <b>${formatTradeValue(this.y)}</b><br/>`,
        ]
      }
      return tooltip.defaultFormatter.call(this, tooltip)
    },
  },
  xAxis: { labels: { format: '{value:%d-%b-%y}' } },
  yAxis: { labels: { format: '{value:.2f}', y: 0, x: 10 }, offset: 30 },

  series: [
    {
      name: `${props.company.name} Stock Price`,
      type: 'area',
      threshold: null,

      data: props.stocks.map(({ date, close }) => [new Date(date).getTime(), close]),

      fillColor: {
        linearGradient: { x1: 0, x2: 0, y1: 0, y2: 1 },
        stops: [
          [0, '#F0FDFA'],
          [1, Highcharts.color('#F0FDFA').setOpacity(0).get('hex')],
        ],
      },
    },
  ],
}))
</script>

<style lang="stylus" scoped>
.stock-info
  display: grid;
  grid-row-gap: 0.25rem;
  grid-column-gap: 2rem;
  grid-template-columns: repeat(2, minmax(auto, max-content));
  grid-template-rows: repeat(3, auto);

@screen md {
  .stock-info {
    grid-column-gap: 0.5rem;
    grid-template-columns: repeat(2, minmax(auto, max-content)) 1fr;
    grid-template-rows: repeat(2, auto);
  }
}
</style>
